import React, {Component, ReactElement, useState} from "react";
import {BORDER_RADIUS, SZ_JUMBO, SZ_SM} from "@/shared/dimens";
import {IconButton, Slider, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledContainer, StyledHeadingVariant} from "@/shared/StyledComponents";
import {PauseOutlined, PlayArrowOutlined} from "@mui/icons-material";
import Markdown from "react-markdown";
import {ShowcaseItem} from "../types";
import {ShowcaseItemImage} from "./ShowcaseItemImage";

function formatTime(time: number): string {
  time = Math.floor(time);
  const minutes = Math.floor(time / 60).toString();
  const seconds = (time % 60).toString();
  return minutes.padStart(2, "0") + ":" + seconds.padStart(2, "0");
}

function AudioPlayerFull(props: {
  audio: HTMLAudioElement,
  item: ShowcaseItem,
  playing: boolean,
  currentTime: number,
  currentPosition: number,
}): ReactElement {
  const audio = props.audio;
  const [draggingValue, setDraggingValue] = useState<number>(-1);
  return <StyledContainer size={"sm"} style={{padding: "4%", flexGrow: 1, justifyContent: "center"}}>
    <ShowcaseItemImage showcaseItem={props.item}
                       style={{width: SZ_JUMBO, alignSelf: "center", borderRadius: BORDER_RADIUS}}/>
    <StyledBoxColumn style={{}}>
      <StyledHeadingVariant default_={5} max={6} style={{textAlign: "center"}}>{props.item.title}</StyledHeadingVariant>
      <Typography component="div" style={{textAlign: "center"}}>
        <Markdown>
          {props.item.description}
        </Markdown>
      </Typography>
    </StyledBoxColumn>
    <StyledBoxRow style={{
      alignItems: "center",
      height: SZ_SM,
      marginBottom: -24,
    }}>
      {props.currentPosition ? <>
          <Slider
            style={{flexGrow: 1}}
            onChange={(event, newValue: number) => setDraggingValue(newValue)}
            onChangeCommitted={(event, newValue: number) => {
              audio.currentTime = (newValue * audio.duration / 100);
              setTimeout(() => setDraggingValue(-1), 500);
            }}
            value={draggingValue >= 0 ? draggingValue : props.currentPosition * 100}
            // value={props.currentPosition * 100}
          />
          <Typography>{formatTime(props.currentTime)}</Typography>
        </>
        : null}
    </StyledBoxRow>
    <StyledBoxRow style={{width: "100%", display: "flex", alignItems: "center", justifyContent: "center"}}>
      <IconButton color="primary" onClick={() => audio.paused ? audio.play() : audio.pause()}>
        {audio.paused ? <PlayArrowOutlined style={{width: 72, height: 72}}/> :
          <PauseOutlined style={{width: 72, height: 72}}/>}
      </IconButton>
    </StyledBoxRow>
  </StyledContainer>;
}

export type AudioPlayerProps = {
  audio: HTMLAudioElement,
  item: ShowcaseItem,
}

type AudioPlayerState = {
  playing: boolean,
  currentTime: number,
  currentPosition: number,
}

export class AudioPlayer extends Component<AudioPlayerProps, AudioPlayerState> {

  constructor(props: AudioPlayerProps, context: any) {
    super(props, context);
    this.state = {
      playing: false,
      currentTime: -1,
      currentPosition: -1,
    }
  }

  componentDidMount() {
    const audio = this.props.audio;
    audio.addEventListener("play", () => {
      this.setState({
        playing: true,
      });
    });
    audio.addEventListener("pause", () => {
      this.setState({
        playing: false,
      });
    });
    this.updateTrack();
    setTimeout(() => audio.play(), 500);
  }

  componentWillUnmount() {
    this.props.audio.pause();
  }

  private updateTimerId: any;

  componentDidUpdate(prevProps: Readonly<AudioPlayerProps>, prevState: Readonly<AudioPlayerState>, snapshot?: any) {
    const audio = this.props.audio;
    if (this.state.playing !== prevState.playing) {
      clearInterval(this.updateTimerId);
      if (this.state.playing) {
        this.updateTimerId = setInterval(() => {
          const currentTime = audio.currentTime;
          const currentPosition = audio.duration > 0 ? Math.min(1, Math.max(0, currentTime / audio.duration)) : 1;
          this.setState({
            currentTime: currentTime,
            currentPosition: currentPosition,
          });
        }, 500);
      }
    }
  }

  private updateTrack(): void {
    const audio = this.props.audio;
    const src = this.props.item.audioData.url;
    if (src) {
      audio.src = src;
      if (this.state.playing) {
        audio.play();
      }
    } else {
      audio.pause();
    }
  }

  render(): ReactElement {
    if (!this.props.item) {
      return null;
    }
    const audio = this.props.audio;
    return <AudioPlayerFull
      audio={audio}
      item={this.props.item}
      playing={this.state.playing}
      currentPosition={this.state.currentPosition}
      currentTime={this.state.currentTime}/>;
  }
}